import React, {useMemo} from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './ArrBarChart.scss';
import {Box} from '@mui/material';
import {t} from 'i18next';

export interface BarChartData {
  label: string;
  value: number;
}

export interface ArrBarChartProps {
  data: BarChartData[];
  xLabel: string;
  barColor?: string;
  maxVisibleItems?: number;
  className?: string;
  dataTestId: string;
}

const ArrBarChart: React.FC<ArrBarChartProps> = ({
  data,
  xLabel,
  barColor = '#6366f1',
  maxVisibleItems = 3,
  className,
  dataTestId,
}) => {
  const barHeight = 24;
  const barGap = 24;
  const maxValue = useMemo(() => {
    return Math.max(...data.map((item) => item.value), 1);
  }, [data]);

  const itemHeight = barHeight + barGap;
  const xAxisSpace = 60;
  const totalHeight = data.length * itemHeight + xAxisSpace;
  const calculatedHeight = Math.max(totalHeight, 180);

  const isScrollable = data.length > maxVisibleItems;

  const options: Highcharts.Options = useMemo(
    () => ({
      chart: {
        type: 'bar',
        backgroundColor: '#182231',

        borderWidth: 1,
        borderRadius: 8,
        height: calculatedHeight,
        spacing: [20, 40, 20, 70], // top, right, bottom, left
        style: {
          fontFamily: 'Inter',
        },
        reflow: false,
      },

      tooltip: {
        enabled: false,
      },
      title: {
        text: undefined,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        categories: data.map((item) => item.label),
        type: 'category',
        lineColor: '#546A8C',
        tickColor: '#546A8C',
        labels: {
          style: {
            color: '#FCFCFD',
            fontSize: '12px',
            fontFamily: 'Inter',
            fontWeight: '600',
          },
          align: 'right',
          x: -8,
        },
        gridLineWidth: 0,

        tickLength: 0,
        tickWidth: 0,
        tickPosition: 'inside',
        tickmarkPlacement: 'on',
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
        lineColor: '#546A8C',
        tickColor: '#546A8C',
        labels: {
          style: {
            color: '#A5B4CB',
            fontSize: '11px',
            fontFamily: 'Inter',
          },
        },
        gridLineColor: '#263449',
        gridLineWidth: 1,
        gridLineDashStyle: 'Solid',
        gridZIndex: 1,
        title: {
          text: xLabel,
          style: {
            color: '#A5B4CB',
            fontSize: '16px',
            fontWeight: '600',
            fontFamily: 'Inter',
          },
          margin: 20,
        },
      },
      plotOptions: {
        bar: {
          color: barColor,
          borderRadius: 2,
          pointPadding: 0.3,
          groupPadding: 0.2,
          pointWidth: 24,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            align: 'right',
            //alignTo: 'plotEdges',
            verticalAlign: 'middle',
            inside: false,
            //overflow: 'allow',

            x: 48,
            style: {
              color: '#FCFCFD',
              fontSize: '11px',
              fontWeight: '600',
              fontFamily: 'Inter',
              textOutline: 'none',
              border: 'none',
            },
            backgroundColor: '#182231',

            borderWidth: 0,
            formatter: function () {
              return this.y?.toString() || '';
            },
          },
          states: {
            hover: {
              brightness: 0.8,
            },
          },

          pointRange: barHeight + barGap,
          minPointLength: 4,
          cropThreshold: 0,
          turboThreshold: 0,
        },
        series: {
          pointPadding: 40,
          groupPadding: 24,
          pointWidth: 240,
          states: {
            hover: {
              enabled: false,
            },
          },
          animation: {
            duration: 300,
          },
        },
      },

      series: [
        {
          pointPadding: 40,
          groupPadding: 24,
          pointWidth: 24,
          name: 'Data',
          type: 'bar',
          data: data.map((item) => ({
            name: item.label,
            y: item.value,
          })),
        },
      ],
      accessibility: {
        enabled: true,
        point: {
          descriptionFormatter: function (point) {
            return `${point.name}: ${point.y}`;
          },
        },
      },
    }),
    [
      data,
      xLabel,
      barColor,
      calculatedHeight,
      maxValue,
      barHeight,
      isScrollable,
      maxVisibleItems,
      itemHeight,
    ]
  );

  if (!data || data.length === 0) {
    return (
      <Box
        className="arr-chart-no-data"
        height={calculatedHeight}
        data-testid="no-chart-data"
      >
        {t('docExplorer.actionMenu.bulkTaggingWarning.defaultText')}
      </Box>
    );
  }

  const callback = (chart: Highcharts.Chart) => {
    if (chart.series?.[0]) {
      const series = chart.series[0];
      const points = series.points;

      if (points) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        points.forEach((point, index) => {
          const element = point.graphic?.element;
          if (element) {
            element.style.height = `${barHeight}px`;
            element.style.marginBottom = `${barGap}px`;
          }
        });
      }
    }
  };

  return (
    <div
      className={`${className} arr-bar-chart`}
      aria-label={`${xLabel} bar chart`}
      data-testid={dataTestId || 'arr-bar-chart-container'}
    >
      {isScrollable ? (
        <Box
          height={`${totalHeight}px`}
          className="arr-chart-inner-container"
          data-testid="chart-inner-container"
          aria-label="Scrollable chart content"
        >
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            callback={callback}
            containerProps={{
              style: {
                width: '100%',
              },
            }}
          />
        </Box>
      ) : (
        <Box
          className="arr-chart-no-scroll"
          data-testid="no-scroll-container"
          aria-label="chart content"
        >
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            callback={callback}
            containerProps={{
              style: {
                width: '100%',
                height: '100%',
              },
            }}
          />
        </Box>
      )}
    </div>
  );
};

export default ArrBarChart;
